import pc from "picocolors";
import { ClaudeCodeAdapter } from "../adapters/claude-code/adapter";
import { openDb } from "../store/db";
import { Store } from "../store/queries";

export interface ScanOptions {
  claudeDir?: string;
  db?: string;
  full?: boolean;
  verbose?: boolean;
  /** Suppress success output (used by `tktuner sync --quiet`). */
  silent?: boolean;
}

export interface ScanResult {
  files: number;
  skipped: number;
  newTurns: number;
  newEvents: number;
  parseErrors: number;
  unknownTypes: Record<string, number>;
  seconds: number;
}

export async function scan(opts: ScanOptions): Promise<ScanResult | null> {
  const adapter = new ClaudeCodeAdapter();
  const det = adapter.detect(opts.claudeDir);
  if (!det.found) {
    console.error(
      pc.red(
        `No Claude Code data found at ${opts.claudeDir ?? "~/.claude/projects"}. ` +
          `Use --claude-dir to point at your Claude projects directory.`,
      ),
    );
    process.exitCode = 1;
    return null;
  }

  const t0 = performance.now();
  const db = openDb(opts.db);
  const store = new Store(db);
  const result: ScanResult = {
    files: 0,
    skipped: 0,
    newTurns: 0,
    newEvents: 0,
    parseErrors: 0,
    unknownTypes: {},
    seconds: 0,
  };

  try {
    const files = adapter.listLogFiles(det.rootDirs);
    for (const file of files) {
      const state = store.getFileState(file.path);
      let fromOffset = 0;
      if (opts.full) {
        store.deleteFileData(file.path);
      } else if (state) {
        if (state.mtimeMs === file.mtimeMs && state.size === file.sizeBytes) {
          result.skipped++;
          continue;
        }
        if (file.sizeBytes >= state.byteOffset) {
          fromOffset = state.byteOffset;
        } else {
          // File shrank: it was rewritten, not appended. Reparse from zero.
          store.deleteFileData(file.path);
        }
      }

      for await (const batch of adapter.parseFile(file, fromOffset)) {
        const ingested = store.ingestBatch(file, batch);
        result.newTurns += ingested.newTurns;
        result.newEvents += ingested.newEvents;
        result.parseErrors += batch.parseErrors;
        for (const [k, v] of Object.entries(batch.unknownTypes)) {
          result.unknownTypes[k] = (result.unknownTypes[k] ?? 0) + v;
        }
      }
      result.files++;
      if (opts.verbose) console.log(pc.dim(`  ${file.kind.padEnd(8)} ${file.path}`));
    }
    store.bumpUnknownTypes(result.unknownTypes);
  } finally {
    db.close();
  }

  result.seconds = (performance.now() - t0) / 1000;
  if (!opts.silent) {
    console.log(
      `${pc.bold("scan complete")} — ${result.files} file(s) parsed, ${result.skipped} unchanged, ` +
        `${pc.green(String(result.newTurns))} new assistant line(s), ${result.newEvents} event(s) in ${result.seconds.toFixed(1)}s`,
    );
    if (result.parseErrors > 0) {
      console.log(pc.yellow(`  ${result.parseErrors} unparseable line(s) skipped`));
    }
    const unknown = Object.entries(result.unknownTypes);
    if (unknown.length > 0) {
      const summary = unknown.map(([k, v]) => `${k}×${v}`).join(", ");
      console.log(
        pc.yellow(`  unknown line type(s) skipped (parser may need an update): ${summary}`),
      );
    }
  }
  return result;
}
